import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { getTransmitterConfigs, pushConfigs } from '../../api';
import type { TransmitterV2Document, TransmitterConfig } from '../../types';
import { getLoRaWANConfigSupport, getLoRaWANOtaNotice } from '../../utils/lorawan-commands';

const DEFAULT_OTA_DELAY_MINUTES = 15;
const ATEK_E5_DEVICE_TYPE = 'atek_e5';
const E5_CALIBRATION_KEY_PATTERN = /^offset_([1-4])(?:_[abc])?$/;
const E5_CALIBRATION_COMPANION_SUFFIXES = ['', '_c', '_b', '_a'] as const;
const MAX_REASONABLE_DECODED_FLOAT = 1000000;
const TS30X_DEVICE_TYPE_PATTERN = /(^|[^a-z0-9])ts30(?:1|2|x)([^a-z0-9]|$)/;
const TS60X_DEVICE_TYPE_PATTERN = /(^|[^a-z0-9])ts60(?:1|2|x)([^a-z0-9]|$)/;
const TS30X_ATTRIBUTE_QUERY_CONFIG_KEY = '__query_ts30x_device_attributes';

interface DeviceConfigDefinition {
  key: string;
  label: string;
  unit?: string;
  t: 'dec' | 'str';
  description: string;
  readOnly?: boolean;
  hidden?: boolean;
  options?: Array<{ value: string; label: string }>;
}

interface DeviceConfigProfile {
  key: 'ts30x' | 'ts60x';
  pattern: RegExp;
  definitions: DeviceConfigDefinition[];
  definitionMap: Map<string, DeviceConfigDefinition>;
  supportsPush: boolean;
}

const TS30X_CONFIG_DEFINITIONS: DeviceConfigDefinition[] = [
  {
    key: 'software_version',
    label: 'Software version',
    t: 'str',
    readOnly: true,
    description: 'Device-reported TS30x software version from attribute 0xFF/0x0A. Read-only.',
  },
  {
    key: 'report_interval',
    label: 'Reporting interval',
    unit: 'min',
    t: 'dec',
    description: 'Periodic uplink interval. TS30x range: 1-1440 minutes.',
  },
  {
    key: 'button_lock',
    label: 'Power button lock',
    t: 'dec',
    description: 'Controls whether the power button can turn the device off or collect/report data.',
    options: [
      { value: '0', label: 'Unlocked' },
      { value: '1', label: 'Lock power off' },
      { value: '2', label: 'Lock collect/report' },
      { value: '3', label: 'Lock both' },
    ],
  },
  {
    key: 'data_storage_enabled',
    label: 'Local data recording',
    t: 'dec',
    description: 'Stores historical records locally on the device.',
    options: [
      { value: '1', label: 'Enabled' },
      { value: '0', label: 'Disabled' },
    ],
  },
  {
    key: 'data_retransmission_enabled',
    label: 'Auto retransmission',
    t: 'dec',
    description: 'Automatic retransmission after outages. Keep disabled when using gap recovery.',
    options: [
      { value: '0', label: 'Disabled' },
      { value: '1', label: 'Enabled' },
    ],
  },
  {
    key: 'data_retransmission_interval_seconds',
    label: 'Retransmission interval',
    unit: 's',
    t: 'dec',
    description: 'Interval used only when auto retransmission is enabled. TS30x range: 30-1200 seconds.',
  },
  {
    key: 'screen_enabled',
    label: 'LCD screen',
    t: 'dec',
    description: 'Controls whether the screen is enabled when a temperature sensor is connected.',
    options: [
      { value: '1', label: 'Enabled' },
      { value: '0', label: 'Disabled' },
      { value: '255', label: 'Auto' },
    ],
  },
  {
    key: 'temperature_unit',
    label: 'Display temperature unit',
    t: 'dec',
    description: 'Affects ToolBox/LCD display only; uplink values remain Celsius.',
    options: [
      { value: '0', label: 'Celsius' },
      { value: '1', label: 'Fahrenheit' },
    ],
  },
  {
    key: 'magnet_alarm_delay_seconds',
    label: 'Magnet alarm delay',
    unit: 's',
    t: 'dec',
    description: 'Door-open alarm delay. 0 disables the delay; otherwise range is 4-65535 seconds.',
  },
  {
    key: 'offset_1',
    label: 'CH1 temperature offset',
    unit: 'deg C',
    t: 'dec',
    description: 'Temperature calibration offset for the first probe channel.',
  },
  {
    key: 'offset_2',
    label: 'CH1 humidity offset',
    unit: '%RH',
    t: 'dec',
    description: 'Humidity calibration offset for the first probe channel.',
  },
];

const TS30X_CONFIG_DEFINITION_MAP = new Map(
  TS30X_CONFIG_DEFINITIONS.map((definition) => [definition.key, definition]),
);

const TS60X_CONFIG_DEFINITIONS: DeviceConfigDefinition[] = [
  {
    key: 'device_status',
    label: 'Device status',
    t: 'dec',
    readOnly: true,
    description: 'Device status reported in the TS60x basic information packet.',
  },
  {
    key: 'battery_pct',
    label: 'Battery level',
    unit: '%',
    t: 'dec',
    readOnly: true,
    description: 'Battery level reported by the TS60x sensor data packet.',
  },
  {
    key: 'serial_number',
    label: 'Serial number',
    t: 'str',
    readOnly: true,
    description: 'TS60x device serial number reported by the device.',
  },
  {
    key: 'hw_version',
    label: 'Hardware version',
    t: 'str',
    readOnly: true,
    description: 'Hardware version reported by the TS60x basic information packet.',
  },
  {
    key: 'sw_version',
    label: 'Software version',
    t: 'str',
    readOnly: true,
    description: 'Software version reported by the TS60x basic information packet.',
  },
  {
    key: 'tsl_version',
    label: 'TSL version',
    t: 'str',
    readOnly: true,
    description: 'TSL version reported by the TS60x basic information packet.',
  },
  {
    key: 'oem_id',
    label: 'OEM ID',
    t: 'str',
    readOnly: true,
    description: 'OEM identifier reported by the TS60x basic information packet.',
  },
  {
    key: 'imei',
    label: 'IMEI',
    t: 'str',
    readOnly: true,
    description: 'Cellular module IMEI reported by the TS60x packet header.',
  },
  {
    key: 'imsi',
    label: 'IMSI',
    t: 'str',
    readOnly: true,
    description: 'Cellular SIM IMSI reported by the TS60x packet header.',
  },
  {
    key: 'iccid',
    label: 'ICCID',
    t: 'str',
    readOnly: true,
    description: 'SIM ICCID reported by the TS60x packet header.',
  },
  {
    key: 'rsrp',
    label: 'Network signal RSRP',
    unit: 'dBm',
    t: 'dec',
    readOnly: true,
    description: 'Cellular signal strength reported by the TS60x packet header.',
  },
  {
    key: 'gps',
    label: 'GPS location',
    t: 'str',
    readOnly: true,
    description: 'GPS coordinates reported by the TS60x sensor data packet when available.',
  },
  {
    key: 'flight_mode',
    label: 'Flight mode',
    t: 'str',
    readOnly: true,
    description: 'Flight mode state reported by the TS60x sensor data packet.',
    options: [
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' },
    ],
  },
  {
    key: 'probe_connected',
    label: 'Probe connected',
    t: 'str',
    readOnly: true,
    description: 'Probe connection state reported by the TS60x sensor data packet.',
    options: [
      { value: 'true', label: 'Connected' },
      { value: 'false', label: 'Disconnected' },
    ],
  },
  {
    key: 'serial_1',
    label: 'Connected probe serial',
    t: 'str',
    readOnly: true,
    description: 'Serial number of the connected TS60x probe reported by the TS602 Probe ID packet. Read-only device metadata.',
  },
  {
    key: 'serial_2',
    label: 'Connected probe serial (humidity channel)',
    t: 'str',
    readOnly: true,
    hidden: true,
    description: 'Secondary channel serial for a connected TS60x TH probe. Read-only device metadata.',
  },
  {
    key: 'report_type',
    label: 'Report type',
    t: 'str',
    readOnly: true,
    description: 'Whether the TS60x uplink was periodic or button-triggered.',
  },
  {
    key: 'time_kind',
    label: 'Timestamp type',
    t: 'str',
    readOnly: true,
    description: 'Whether the TS60x timestamp represents a periodic or retransmitted record.',
  },
  {
    key: 'time_utc',
    label: 'Device timestamp',
    t: 'str',
    readOnly: true,
    description: 'Timestamp embedded in the TS60x sensor data packet.',
  },
];

const TS60X_CONFIG_DEFINITION_MAP = new Map(
  TS60X_CONFIG_DEFINITIONS.map((definition) => [definition.key, definition]),
);

const DEVICE_CONFIG_PROFILES: DeviceConfigProfile[] = [
  {
    key: 'ts30x',
    pattern: TS30X_DEVICE_TYPE_PATTERN,
    definitions: TS30X_CONFIG_DEFINITIONS,
    definitionMap: TS30X_CONFIG_DEFINITION_MAP,
    supportsPush: true,
  },
  {
    key: 'ts60x',
    pattern: TS60X_DEVICE_TYPE_PATTERN,
    definitions: TS60X_CONFIG_DEFINITIONS,
    definitionMap: TS60X_CONFIG_DEFINITION_MAP,
    supportsPush: false,
  },
];

// =============================================================================
// Props
// =============================================================================

interface ConfigsOtaSectionProps {
  transmitter: TransmitterV2Document;
  onRefresh: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Auto-infer config type from key prefix when type is not already set.
 * Matches legacy categoriseConfigs behaviour:
 * - wifi (no underscore), serial_ -> str
 * - offset, alert, send_, report_, data_, auto_, *_calibration -> dec
 * - all others -> hex
 */
function inferConfigType(key: string): 'hex' | 'dec' | 'str' {
  const k = key.toLowerCase();
  if (k.startsWith('wifi') || k.startsWith('serial_')) return 'str';
  if (
    k.startsWith('offset') ||
    k.startsWith('alert') ||
    k.startsWith('send_') ||
    k.startsWith('report_') ||
    k.startsWith('data_') ||
    k.startsWith('auto_') ||
    k.endsWith('_calibration')
  ) return 'dec';
  return 'hex';
}

function parseConfigJson(
  value: TransmitterV2Document['configjson'] | Record<string, unknown> | string | null | undefined,
): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function isDeviceConfigAttributeKey(key: string): boolean {
  return key !== 'device_type'
    && key !== 'deviceType'
    && key !== 'product_id'
    && !key.startsWith('packet_');
}

function isProbeSerialConfigKey(key: string): boolean {
  return /^serial_\d+$/.test(key);
}

function getDeviceConfigProfile(
  transmitter: TransmitterV2Document,
): DeviceConfigProfile | undefined {
  const configJson = parseConfigJson(transmitter.configjson);
  const deviceType = String(
    transmitter.device_type
    || ('deviceType' in transmitter ? transmitter.deviceType : '')
    || configJson?.device_type
    || configJson?.deviceType
    || '',
  ).toLowerCase();

  return DEVICE_CONFIG_PROFILES.find((profile) => profile.pattern.test(deviceType));
}

function ensureDeviceProfileConfigs(
  transmitter: TransmitterV2Document,
  configs: TransmitterConfig[],
): TransmitterConfig[] {
  const profile = getDeviceConfigProfile(transmitter);
  if (!profile) return configs;

  const next = [...configs];
  const existingByKey = new Map(next.map((config, index) => [config.k, { config, index }]));

  profile.definitions.forEach((definition) => {
    const existing = existingByKey.get(definition.key);
    if (existing) {
      const config = {
        ...existing.config,
        v: existing.config.v ?? '',
        t: definition.t,
      };
      if (definition.readOnly) {
        delete config.s;
      } else {
        config.s = existing.config.s ?? '';
      }
      next[existing.index] = config;
      return;
    }

    if (definition.hidden) return;

    const config: TransmitterConfig = {
      k: definition.key,
      v: '',
      t: definition.t,
    };
    if (!definition.readOnly) config.s = '';
    next.push(config);
  });

  return next;
}

/**
 * Initialize local config entries from the transmitter document.
 * Auto-infers `t` if not already set.
 */
function initConfigs(transmitter: TransmitterV2Document): TransmitterConfig[] {
  const normalizedTransmitter = withConfigJsonDeviceType(transmitter, transmitter.configjson);
  const profile = getDeviceConfigProfile(normalizedTransmitter);
  const configs = mergeReportedConfigJson(transmitter.configs || [], transmitter.configjson).map((c) => ({
    ...c,
    t: profile?.definitionMap.get(c.k)?.t || c.t || inferConfigType(c.k),
  }));

  return ensureDeviceProfileConfigs(normalizedTransmitter, configs);
}

function isAtekE5(transmitter: TransmitterV2Document): boolean {
  return transmitter.device_type === ATEK_E5_DEVICE_TYPE;
}

function hasDesiredChange(config: TransmitterConfig): boolean {
  const desired = config.s;
  return desired !== undefined
    && desired.trim() !== ''
    && desired !== config.v;
}

function formatNumberForConfig(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(6)));
}

function decodePossiblyRawFloat(value: string | undefined): string | undefined {
  if (value === undefined || value.trim() === '') return undefined;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (!Number.isInteger(numeric) || numeric < 0 || numeric > 0xffffffff) {
    return value;
  }
  if (numeric === 0) return '0';

  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  view.setUint32(0, numeric, false);
  const decoded = view.getFloat32(0, false);

  if (!Number.isFinite(decoded)) return undefined;
  if (Math.abs(decoded) > MAX_REASONABLE_DECODED_FLOAT) return value;
  return formatNumberForConfig(decoded);
}

function getE5CalibrationInputIndex(key: string): string | undefined {
  return key.match(E5_CALIBRATION_KEY_PATTERN)?.[1];
}

function getE5CalibrationCompanionKeys(inputIndex: string): string[] {
  return E5_CALIBRATION_COMPANION_SUFFIXES.map((suffix) => `offset_${inputIndex}${suffix}`);
}

function getCompanionDesiredValue(config: TransmitterConfig): string | undefined {
  if (config.s !== undefined && config.s.trim() !== '') return config.s;
  if (getE5CalibrationInputIndex(config.k)) {
    return decodePossiblyRawFloat(config.v);
  }
  return config.v;
}

function prepareAtekE5ConfigsForPush(configs: TransmitterConfig[]): TransmitterConfig[] {
  const changedKeys = new Set(configs.filter(hasDesiredChange).map((config) => config.k));
  if (changedKeys.size === 0) return [];

  const next: TransmitterConfig[] = configs.map((config) => {
    if (changedKeys.has(config.k)) return config;
    const rest = { ...config };
    delete rest.s;
    return rest;
  });
  const byKey = new Map(next.map((config, index) => [config.k, { config, index }]));

  for (const key of changedKeys) {
    const inputIndex = getE5CalibrationInputIndex(key);
    if (!inputIndex) continue;

    for (const companionKey of getE5CalibrationCompanionKeys(inputIndex)) {
      const entry = byKey.get(companionKey);
      if (!entry) continue;
      const desired = getCompanionDesiredValue(entry.config);
      if (desired !== undefined) {
        next[entry.index] = { ...entry.config, s: desired };
      }
    }
  }

  return next;
}

function getConfigsForPush(
  transmitter: TransmitterV2Document,
  configs: TransmitterConfig[],
): TransmitterConfig[] {
  const profile = getDeviceConfigProfile(transmitter);
  const pushableConfigs = profile
    ? configs.filter((config) => profile.supportsPush && !profile.definitionMap.get(config.k)?.readOnly)
    : configs;

  if (!isAtekE5(transmitter)) return pushableConfigs;

  return prepareAtekE5ConfigsForPush(pushableConfigs);
}

function stringifyConfigValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function addUniqueProbeSerial(serials: string[], value: unknown): void {
  const serial = stringifyConfigValue(value).trim();
  if (!serial || serial === '0' || serials.includes(serial)) return;
  serials.push(serial);
}

function getTransmitterProbeSerials(
  transmitter: TransmitterV2Document,
  configs: TransmitterConfig[],
): string[] {
  const serials: string[] = [];

  (transmitter.streams || []).forEach((stream) => {
    addUniqueProbeSerial(serials, stream.probe_serial ?? stream.probeSerial);
  });

  const configJson = parseConfigJson(transmitter.configjson);
  if (configJson) {
    Object.entries(configJson).forEach(([key, value]) => {
      if (isProbeSerialConfigKey(key)) addUniqueProbeSerial(serials, value);
    });
  }

  configs.forEach((config) => {
    if (isProbeSerialConfigKey(config.k)) {
      addUniqueProbeSerial(serials, config.v ?? config.s);
    }
  });

  return serials;
}

function normalizeConfigArray(configs: TransmitterConfig[] | Record<string, unknown> | null | undefined): TransmitterConfig[] {
  if (Array.isArray(configs)) {
    return configs.map((config) => ({
      ...config,
      t: config.t || inferConfigType(config.k),
    }));
  }

  if (configs && typeof configs === 'object') {
    return Object.entries(configs).map(([key, value]) => ({
      k: key,
      s: stringifyConfigValue(value),
      v: '',
      t: inferConfigType(key),
    }));
  }

  return [];
}

function mergeConfirmedConfigs(
  desiredConfigs: TransmitterConfig[],
  confirmedConfigs: Record<string, unknown> | null | undefined,
): TransmitterConfig[] {
  if (!confirmedConfigs || typeof confirmedConfigs !== 'object' || Array.isArray(confirmedConfigs)) {
    return desiredConfigs;
  }

  const confirmedByKey = new Map(
    Object.entries(confirmedConfigs).map(([key, value]) => [key, stringifyConfigValue(value)]),
  );
  const merged = desiredConfigs.map((config) => {
    if (!config.k || !confirmedByKey.has(config.k)) return config;
    return { ...config, v: confirmedByKey.get(config.k) };
  });
  const desiredKeys = new Set(desiredConfigs.map((config) => config.k).filter(Boolean));

  confirmedByKey.forEach((value, key) => {
    if (!desiredKeys.has(key)) {
      merged.push({
        k: key,
        s: '',
        v: value,
        t: inferConfigType(key),
      });
    }
  });

  return merged;
}

function mergeReportedConfigJson(
  configs: TransmitterConfig[],
  configJsonValue: TransmitterV2Document['configjson'] | Record<string, unknown> | string | null | undefined,
): TransmitterConfig[] {
  const configJson = parseConfigJson(configJsonValue);
  if (!configJson) return configs;

  const attributes = Object.entries(configJson).reduce<Record<string, unknown>>((mapped, [key, value]) => {
    if (isDeviceConfigAttributeKey(key)) mapped[key] = value;
    return mapped;
  }, {});

  return mergeConfirmedConfigs(configs, attributes);
}

function withConfigJsonDeviceType(
  transmitter: TransmitterV2Document,
  configJsonValue: TransmitterV2Document['configjson'] | Record<string, unknown> | string | null | undefined,
): TransmitterV2Document {
  if (transmitter.device_type) return transmitter;
  const configJson = parseConfigJson(configJsonValue);
  const deviceType = configJson?.device_type || configJson?.deviceType;
  return typeof deviceType === 'string'
    ? { ...transmitter, device_type: deviceType }
    : transmitter;
}

function formatDeviceConfigValue(
  definition: DeviceConfigDefinition | undefined,
  value: string | undefined,
): string {
  if (value === undefined || value === null || value === '') return '--';

  const option = definition?.options?.find((item) => item.value === String(value));
  if (option) return option.label;

  return `${value}${definition?.unit ? ` ${definition.unit}` : ''}`;
}

function formatDeviceConfigCurrentValue(
  transmitter: TransmitterV2Document,
  profile: DeviceConfigProfile | undefined,
  definition: DeviceConfigDefinition | undefined,
  config: TransmitterConfig,
  configs: TransmitterConfig[],
): string {
  if (profile?.key === 'ts60x' && definition?.key === 'serial_1') {
    const probeConnected = configs.find((candidate) => candidate.k === 'probe_connected');
    const isExplicitDisconnected = ['false', '0', 'disconnected'].includes(String(probeConnected?.v || '').toLowerCase());
    const probeSerials = isExplicitDisconnected ? [] : getTransmitterProbeSerials(transmitter, configs);
    if (probeSerials.length) return probeSerials.join(', ');
  }

  return formatDeviceConfigValue(definition, config.v);
}

function getConfigRows(
  configs: TransmitterConfig[],
  profile: DeviceConfigProfile | undefined,
): Array<{ config: TransmitterConfig; index: number }> {
  if (!profile) {
    return configs.map((config, index) => ({ config, index }));
  }

  const usedIndexes = new Set<number>();
  const rows: Array<{ config: TransmitterConfig; index: number }> = [];

  profile.definitions.forEach((definition) => {
    const index = configs.findIndex((config, configIndex) => (
      !usedIndexes.has(configIndex) && config.k === definition.key
    ));
    if (index < 0) return;
    usedIndexes.add(index);
    if (definition.hidden) return;
    rows.push({ config: configs[index], index });
  });

  configs.forEach((config, index) => {
    if (!usedIndexes.has(index)) rows.push({ config, index });
  });

  return rows;
}

// =============================================================================
// Super Admin View
// =============================================================================

function SuperAdminConfigEditor({
  transmitter,
  onRefresh,
}: ConfigsOtaSectionProps) {
  const [configs, setConfigs] = useState<TransmitterConfig[]>(() =>
    initConfigs(transmitter),
  );
  const [otaDelay, setOtaDelay] = useState<number>(
    transmitter.ota_delay_minutes ?? DEFAULT_OTA_DELAY_MINUTES,
  );
  const [isPushing, setIsPushing] = useState(false);
  const [isRequestingAttributes, setIsRequestingAttributes] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoLoadedDevIdRef = useRef<string | null>(null);
  const profiledTransmitter = useMemo(
    () => withConfigJsonDeviceType(transmitter, transmitter.configjson),
    [transmitter],
  );

  // Re-sync when transmitter data refreshes
  useEffect(() => {
    setConfigs(initConfigs(transmitter));
    setOtaDelay(transmitter.ota_delay_minutes ?? DEFAULT_OTA_DELAY_MINUTES);
  }, [profiledTransmitter, transmitter]);

  const loRaWanNotice = transmitter.downlink_id
    ? getLoRaWANOtaNotice(configs, {
        deviceType: profiledTransmitter.device_type,
        transmissionIntervalSeconds: transmitter.transmission_interval_seconds,
      })
    : null;
  const deviceConfigProfile = getDeviceConfigProfile(profiledTransmitter);
  const hasDeviceConfigProfile = Boolean(deviceConfigProfile);
  const readOnlyConfigProfile = deviceConfigProfile?.supportsPush === false;
  const configRows = getConfigRows(configs, deviceConfigProfile);

  const updateConfig = (index: number, field: keyof TransmitterConfig, value: string) => {
    setConfigs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeConfig = (index: number) => {
    setConfigs((prev) => prev.filter((_, i) => i !== index));
  };

  const addConfig = () => {
    setConfigs((prev) => [...prev, { k: '', s: '', v: '', t: 'hex' }]);
  };

  const refreshDeviceConfigs = useCallback(async (showToast: boolean) => {
    setIsRefreshing(true);
    try {
      const result = await getTransmitterConfigs(transmitter.dev_id);
      setConfigs((currentConfigs) => {
        const refreshedTransmitter = withConfigJsonDeviceType(
          profiledTransmitter,
          result.configJson ?? transmitter.configjson,
        );
        const desiredConfigs = normalizeConfigArray(result.configs);
        const baseConfigs = desiredConfigs.length > 0 ? desiredConfigs : currentConfigs;
        const reportedConfigs = mergeReportedConfigJson(baseConfigs, result.configJson ?? transmitter.configjson);
        return ensureDeviceProfileConfigs(
          refreshedTransmitter,
          mergeConfirmedConfigs(reportedConfigs, result.confirmedConfigs),
        );
      });
      if (showToast) {
        toast.success('Configs refreshed', {
          description: result.confirmedConfigsAt
            ? `Last device report: ${new Date(result.confirmedConfigsAt).toLocaleString()}`
            : undefined,
        });
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to refresh configs', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [profiledTransmitter, transmitter]);

  useEffect(() => {
    if (!transmitter.dev_id || autoLoadedDevIdRef.current === transmitter.dev_id) return;
    autoLoadedDevIdRef.current = transmitter.dev_id;
    void refreshDeviceConfigs(false);
  }, [refreshDeviceConfigs, transmitter.dev_id]);

  const handleRefreshConfigs = () => {
    void refreshDeviceConfigs(true);
  };

  const handleRequestDeviceAttributes = async () => {
    if (deviceConfigProfile?.key !== 'ts30x') return;

    const configsForPush = [
      ...getConfigsForPush(
        profiledTransmitter,
        configs.filter((config) => config.k.trim() !== '' && config.k !== TS30X_ATTRIBUTE_QUERY_CONFIG_KEY),
      ),
      {
        k: TS30X_ATTRIBUTE_QUERY_CONFIG_KEY,
        s: '1',
        v: '',
        t: 'dec',
      },
    ];

    setIsRequestingAttributes(true);
    try {
      const result = await pushConfigs(
        transmitter._id,
        {
          configs: configsForPush,
          remote_config_enabled: true,
          ota_config_attempts: 0,
          last_config_attempt: null,
          to_config: true,
          ota_delay_minutes: otaDelay,
        },
      );
      if (result.otaWarning) {
        toast.warning('Device attribute request saved but OTA not queued', { description: result.otaWarning });
      } else {
        toast.success('Device attribute request queued');
      }
      onRefresh();
    } catch (error) {
      toast.error('Failed to request device attributes', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsRequestingAttributes(false);
    }
  };

  const handlePushConfigs = async () => {
    if (readOnlyConfigProfile) {
      toast.error('TS60x MQTT devices do not support OTA config downlinks');
      return;
    }

    // Filter out entries with empty keys
    const validConfigs = configs.filter((c) => c.k.trim() !== '');
    const configsForPush = getConfigsForPush(profiledTransmitter, validConfigs);
    if (configsForPush.length === 0) {
      toast.error(isAtekE5(profiledTransmitter) ? 'No config changes to push' : 'No valid config entries to push');
      return;
    }

    setIsPushing(true);
    try {
      const result = await pushConfigs(
        transmitter._id,
        {
          configs: configsForPush,
          remote_config_enabled: true,
          ota_config_attempts: 0,
          last_config_attempt: null,
          to_config: true,
          ota_delay_minutes: otaDelay,
        },
      );
      if (result.otaWarning) {
        toast.warning('Config saved but OTA not queued', { description: result.otaWarning });
      } else {
        toast.success('Config push initiated');
      }
      onRefresh();
    } catch (error) {
      toast.error('Failed to push configs', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsPushing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Configs OTA
      </h3>

      {/* OTA Config Delay */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>OTA Delay (min)</Label>
        <div className="space-y-1">
          <Input
            type="number"
            min={1}
            max={1440}
            value={otaDelay}
            onChange={(e) => setOtaDelay(Number(e.target.value) || DEFAULT_OTA_DELAY_MINUTES)}
            className="w-28 h-8"
          />
          <p className="text-xs text-muted-foreground">
            Delay in minutes between OTA config retry attempts.
          </p>
        </div>
      </div>

      {/* Configs Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{hasDeviceConfigProfile ? 'Attribute / Key' : 'Key'}</TableHead>
              <TableHead className="w-24">Type</TableHead>
              <TableHead>{hasDeviceConfigProfile ? 'Desired' : 'Spec/Desired'}</TableHead>
              <TableHead>{hasDeviceConfigProfile ? 'Current' : 'Value'}</TableHead>
              {hasDeviceConfigProfile && <TableHead>Notes</TableHead>}
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {configRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasDeviceConfigProfile ? 6 : 5} className="text-center text-sm text-muted-foreground py-4">
                  No config entries. Click "Add Config" to start.
                </TableCell>
              </TableRow>
            ) : (
              configRows.map(({ config, index }) => {
                const deviceDefinition = deviceConfigProfile?.definitionMap.get(config.k);
                const configSupport = transmitter.downlink_id
                  ? getLoRaWANConfigSupport(config, transmitter.device_type)
                  : { status: 'automated' as const };
                const desiredValueReadOnly = readOnlyConfigProfile
                  || deviceDefinition?.readOnly
                  || (!deviceDefinition && configSupport.status !== 'automated');
                const supportTextClass = configSupport.status === 'metadata'
                  ? 'text-muted-foreground'
                  : 'text-amber-700';

                return (
                  <TableRow key={`${config.k || 'config'}-${index}`}>
                  <TableCell>
                    {deviceDefinition ? (
                      <div>
                        <div className="font-medium">{deviceDefinition.label}</div>
                        <div className="text-xs text-muted-foreground">{deviceDefinition.key}</div>
                      </div>
                    ) : (
                      <Input
                        value={config.k}
                        onChange={(e) => updateConfig(index, 'k', e.target.value)}
                        disabled={!!config.v || readOnlyConfigProfile}
                        placeholder="Config key"
                        className="h-8"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {deviceDefinition ? (
                      <div className="flex h-8 items-center rounded-md border border-input bg-muted px-3 text-sm">
                        {deviceDefinition.t}
                      </div>
                    ) : (
                      <Select
                        value={config.t || 'hex'}
                        onValueChange={(value) => updateConfig(index, 't', value)}
                        disabled={desiredValueReadOnly}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hex">hex</SelectItem>
                          <SelectItem value="dec">dec</SelectItem>
                          <SelectItem value="str">str</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    {deviceDefinition?.readOnly ? (
                      <div className="flex h-8 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                        --
                      </div>
                    ) : deviceDefinition?.options ? (
                      <Select
                        value={config.s || undefined}
                        onValueChange={(value) => updateConfig(index, 's', value)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="-" />
                        </SelectTrigger>
                        <SelectContent>
                          {deviceDefinition.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex">
                        <Input
                          value={config.s || ''}
                          onChange={(e) => updateConfig(index, 's', e.target.value)}
                          placeholder="Desired value"
                          className="h-8"
                          disabled={desiredValueReadOnly}
                          title={deviceDefinition ? undefined : configSupport.reason}
                        />
                        {deviceDefinition?.unit && (
                          <span className="flex h-8 items-center rounded-r-md border border-l-0 border-input bg-muted px-2 text-xs text-muted-foreground">
                            {deviceDefinition.unit}
                          </span>
                        )}
                      </div>
                    )}
                    {!deviceDefinition && configSupport.reason && (
                      <p className={`mt-1 text-xs ${supportTextClass}`}>
                        {configSupport.reason}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDeviceConfigCurrentValue(
                      profiledTransmitter,
                      deviceConfigProfile,
                      deviceDefinition,
                      config,
                      configs,
                    )}
                  </TableCell>
                  {hasDeviceConfigProfile && (
                    <TableCell className="text-sm text-muted-foreground">
                      {deviceDefinition?.description || 'Custom config'}
                    </TableCell>
                  )}
                  <TableCell>
                    {config.v === '' && !readOnlyConfigProfile && !deviceDefinition?.readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeConfig(index)}
                        className="h-8 w-8"
                        title="Remove config entry"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {!readOnlyConfigProfile && (
          <Button type="button" variant="outline" size="sm" onClick={addConfig}>
            <Plus className="h-4 w-4 mr-1" />
            Add Config
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={handleRefreshConfigs} disabled={isRefreshing}>
          <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
        {deviceConfigProfile?.key === 'ts30x' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRequestDeviceAttributes}
            disabled={isRequestingAttributes}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isRequestingAttributes ? 'animate-spin' : ''}`} />
            {isRequestingAttributes ? 'Requesting...' : 'Request Attributes'}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          onClick={handlePushConfigs}
          disabled={isPushing || readOnlyConfigProfile}
          title={
            readOnlyConfigProfile
              ? 'TS60x MQTT devices do not support OTA config downlinks.'
              : loRaWanNotice?.buttonTitle
          }
        >
          {isPushing ? 'Pushing...' : 'Push Configs'}
        </Button>
      </div>

      {/* Pending config indicator */}
      {transmitter.to_config && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
          {transmitter.downlink_id
            ? loRaWanNotice?.pendingText ?? 'Config update queued - delivery timing depends on the device model and uplink cadence.'
            : 'Config update pending — transmitter will apply on next check-in.'}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Non-Super Admin View (send_lo only — sends full configs array)
// =============================================================================

function LimitedConfigEditor({
  transmitter,
  onRefresh,
}: ConfigsOtaSectionProps) {
  // Keep a local copy of ALL configs so pushConfigs sends the full array
  const [configs, setConfigs] = useState<TransmitterConfig[]>(() =>
    initConfigs(transmitter),
  );
  const [isPushing, setIsPushing] = useState(false);
  const profiledTransmitter = useMemo(
    () => withConfigJsonDeviceType(transmitter, transmitter.configjson),
    [transmitter],
  );

  // Re-sync when transmitter refreshes
  useEffect(() => {
    setConfigs(initConfigs(transmitter));
  }, [transmitter]);

  const loRaWanNotice = transmitter.downlink_id
    ? getLoRaWANOtaNotice(configs, {
        deviceType: profiledTransmitter.device_type,
        transmissionIntervalSeconds: transmitter.transmission_interval_seconds,
      })
    : null;

  const sendLoConfig = configs.find((c) => c.k === 'send_lo');
  const desiredValue = sendLoConfig?.s || '';
  const readOnlyConfigProfile = getDeviceConfigProfile(profiledTransmitter)?.supportsPush === false;

  // If no send_lo exists, render nothing
  if (readOnlyConfigProfile || !transmitter.configs?.some((c) => c.k === 'send_lo')) return null;

  const numericValue = Number(desiredValue);
  const showWarning = desiredValue !== '' && !isNaN(numericValue) && numericValue < 300;

  const updateSendLo = (value: string) => {
    setConfigs((prev) =>
      prev.map((c) => (c.k === 'send_lo' ? { ...c, s: value } : c)),
    );
  };

  const handlePushConfigs = async () => {
    if (!desiredValue.trim()) {
      toast.error('Please enter a desired value');
      return;
    }

    const configsForPush = getConfigsForPush(profiledTransmitter, configs);
    if (configsForPush.length === 0) {
      toast.error('No config changes to push');
      return;
    }

    setIsPushing(true);
    try {
      const result = await pushConfigs(
        transmitter._id,
        {
          configs: configsForPush,
          remote_config_enabled: true,
          ota_config_attempts: 0,
          last_config_attempt: null,
          to_config: true,
        },
      );
      if (result.otaWarning) {
        toast.warning('Config saved but OTA not queued', { description: result.otaWarning });
      } else {
        toast.success('Config push initiated');
      }
      onRefresh();
    } catch (error) {
      toast.error('Failed to push configs', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsPushing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Configs OTA
      </h3>

      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>send_lo</Label>
        <div className="space-y-1">
          <Input
            type="number"
            value={desiredValue}
            onChange={(e) => updateSendLo(e.target.value)}
            placeholder={sendLoConfig?.v ? `Current: ${sendLoConfig.v}` : 'Desired value'}
            className="w-40 h-8"
          />
          {showWarning && (
            <p className="text-xs text-red-500">
              Warning: values below 300 may cause excessive transmission frequency.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={handlePushConfigs}
          disabled={isPushing || !desiredValue.trim()}
          title={loRaWanNotice?.buttonTitle}
        >
          {isPushing ? 'Pushing...' : 'Push Configs'}
        </Button>
      </div>

      {transmitter.to_config && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
          {transmitter.downlink_id
            ? loRaWanNotice?.pendingText ?? 'Config update queued - delivery timing depends on the device model and uplink cadence.'
            : 'Config update pending — transmitter will apply on next check-in.'}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Export
// =============================================================================

export function ConfigsOtaSection(props: ConfigsOtaSectionProps) {
  const isSuperAdmin = useIsSuperAdmin();

  if (isSuperAdmin) {
    return <SuperAdminConfigEditor {...props} />;
  }

  return <LimitedConfigEditor {...props} />;
}
